/*
 * Copyright (c) 2025. World Wide Generation Ltd
 */

import {
  TextNode,
  SerializedTextNode,
  EditorConfig,
  LexicalNode,
  NodeKey,
  Spread,
  IS_BOLD,
  IS_ITALIC,
  IS_UNDERLINE,
} from 'lexical';
import { VariableKey } from '../types';

export type SerializedVariableNode = Spread<
  {
    variableKey: VariableKey;
  },
  SerializedTextNode
>;

export class VariableNode extends TextNode {
  private __variableKey: VariableKey;

  constructor(variableKey: VariableKey, text: string, key?: NodeKey) {
    super(text, key);
    this.__variableKey = variableKey;
    this.__mode = 2;
  }

  static getType(): string {
    return 'variable';
  }

  static clone(node: VariableNode): VariableNode {
    const clonedNode = new VariableNode(node.__variableKey, node.__text, node.__key);
    clonedNode.__format = node.__format;
    return clonedNode;
  }

  createDOM(config: EditorConfig): HTMLElement {
    const element = super.createDOM(config);
    element.className = 'variable-node';
    element.setAttribute('data-variable-key', this.__variableKey);
    element.setAttribute('data-lexical-variable', 'true');
    this.updateDOMClasses(element);
    return element;
  }

  updateDOM(prevNode: VariableNode, element: HTMLElement, config: EditorConfig): boolean {
    const prevFormat = prevNode.getFormat();
    const nextFormat = this.getFormat();

    if (prevFormat !== nextFormat) {
      this.updateDOMClasses(element);
      return false;
    }

    return super.updateDOM(prevNode as unknown as this, element, config);
  }

  private updateDOMClasses(element: HTMLElement): void {
    const format = this.getFormat();

    const isBold = (format & IS_BOLD) !== 0;
    const isItalic = (format & IS_ITALIC) !== 0;
    const isUnderline = (format & IS_UNDERLINE) !== 0;

    if (isBold) {
      element.classList.add('variable-node--bold');
    } else {
      element.classList.remove('variable-node--bold');
    }

    if (isItalic) {
      element.classList.add('variable-node--italic');
    } else {
      element.classList.remove('variable-node--italic');
    }

    if (isUnderline) {
      element.classList.add('variable-node--underline');
    } else {
      element.classList.remove('variable-node--underline');
    }
  }

  static importJSON(serializedNode: SerializedVariableNode): VariableNode {
    const node = $createVariableNode(serializedNode.variableKey);
    node.setFormat(serializedNode.format);
    node.setDetail(serializedNode.detail);
    node.setMode(serializedNode.mode);
    return node;
  }

  exportJSON(): SerializedVariableNode {
    const exported = super.exportJSON();
    // Don't export style - use CSS classes instead
    return {
      ...exported,
      style: '', // Clear any inherited styles
      type: 'variable',
      variableKey: this.__variableKey,
      version: 1,
    };
  }

  getVariableKey(): VariableKey {
    return this.__variableKey;
  }

  canInsertTextBefore(): boolean {
    return false;
  }

  canInsertTextAfter(): boolean {
    return true;
  }

  isTextEntity(): boolean {
    return true;
  }

  isToken(): boolean {
    return true;
  }

  isSegmented(): boolean {
    return false;
  }

  isSimpleText(): boolean {
    return false;
  }
}

export const $createVariableNode = (variableKey: VariableKey): VariableNode => {
  const displayText = `{{${variableKey}}}`;
  return new VariableNode(variableKey, displayText);
};

export const $isVariableNode = (node: LexicalNode | null | undefined): node is VariableNode => {
  return node instanceof VariableNode;
};
